'use client';

import React, { useRef, useEffect } from "react";
// import BenefitColumns from "../components/BenefitColumns/BenefitColumns";
import { LanguageType, LanguageDirection } from "@/lib/types";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
// import SplitType from 'split-type';
import { useGSAP } from "@gsap/react";
import Subheading from "@/components/ui/Subheading/Subheading";
import { Check } from "@phosphor-icons/react";
import { useIsLg, useIsTb } from '@/contexts/BreakpointContext';

gsap.registerPlugin(useGSAP, ScrollTrigger);

interface TextBenefit {
  title: string;
  content: string;
}

interface WaitlistSectionProps {
  language?: LanguageType
  text: string
  showLogo: boolean
  benefits: TextBenefit[]
  onSectionRefReady?: (ref: React.RefObject<HTMLDivElement | null>) => void
  subheading?: string
  column1Heading?: string
  column1Description?: string
  column1Benefits?: TextBenefit[]
  column2Heading?: string
  column2Description?: string
  column2Benefits?: TextBenefit[]
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};

const WaitlistSection: React.FC<WaitlistSectionProps> = ({
  language = "english",
  text,
  showLogo,
  benefits,
  onSectionRefReady,
  subheading = "TWO WAYS TO JOIN US",
  column1Heading = "Join the waitlist",
  column1Description = "Be among the first to experience Rivive through our private waitlist. No special requirements, just an early chance to enjoy personalized, non-invasive care once capacity allows.",
  column1Benefits = [],
  column2Heading = "Become a Founding Member",
  column2Description = "With only 50 available slots, a Founding Membership will mark you as part of Rivive's founding story and unlock privileges never to be offered again. Enrollment requires a Premium/VIP plan.",
  column2Benefits = [],
  // aspectRatio
}) => {
  const isLg = useIsLg();
  const isTb = useIsTb();
  const sectionRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);
  const subheadingRef = useRef<HTMLDivElement>(null);
  const columnsContainerRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);



  useEffect(() => {
    if (sectionRef.current && onSectionRefReady) {
      onSectionRefReady(sectionRef);
    }
  }, [onSectionRefReady]);

  useGSAP(() => {
    const section = sectionRef.current;
    const textElement = textRef.current;
    const subheadingElement = subheadingRef.current;
    const columnsContainer = columnsContainerRef.current;

    if (!section || !textElement || !columnsContainer || !subheadingElement) return;

    const columns = columnsContainer.children;
    if (!columns || columns.length === 0) return;

    // Collect elements to animate
    const elements = [textElement, subheadingElement].filter(Boolean);

    // Set initial states - blur effect without translation
    gsap.set(elements, {
      autoAlpha: 0,
      filter: 'blur(30px)',
      force3D: true,
    });

    gsap.set(columns, {
      autoAlpha: 0,
      filter: 'blur(30px)',
      force3D: true,
    });

    if (isLg) {
      // DESKTOP LOGIC

      // Create scroll trigger animation
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top bottom-=25%",
          toggleActions: "play none none none"
        }
      });

      // Animate heading first
      tl.to(elements, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        duration: 1.0,
        stagger: 0.2,
        ease: "power2.out",
        force3D: true,
      });

      // Then animate columns with stagger
      tl.to(columns, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        duration: 0.8,
        stagger: 0.2,
        ease: "power2.out",
        force3D: true,
      }, "-=0.5"); // Start slightly before heading finishes

    } else {
      // MOBILE LOGIC

      // Text and subheading animation (separate trigger)
      gsap.to(elements, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        duration: 1.0,
        stagger: 0.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: textElement,
          start: "top 85%",
          toggleActions: "play none none none"
        },
        force3D: true,
      });

      // Columns animation (separate trigger)
      gsap.to(columns, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        duration: 1.0,
        stagger: 0.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: columnsContainer,
          start: "top 70%",
          toggleActions: "play none none none"
        },
        force3D: true,
      });
    }

  }, { dependencies: [text, isLg], revertOnUpdate: true });

  return (
    // section has custom padding!
    <section
      ref={sectionRef}
      // py-[2rem]
      // tb:py-[3.5rem]
      className="
      relative
      w-full
      flex items-center justify-center
      px-[1.25rem]
      padding-main-x-desktop

      pt-[2rem]
      tb:py-[3.5rem]
      ">
          <div
        // ref={shadowRef}
        className="absolute w-full aspect-3/4 tb:aspect-3/2 z-10 opacity-[8%] tb:opacity-10 pointer-events-none"
        style={{
          top: '0px',
          transform: '',
          backgroundImage: `url(./Website-Shadow-Overlay.avif)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          mixBlendMode: 'darken'
        }}
      />


      <div className="
        relative
        z-10
        max-w-[100%]
        container-small
        ">
        <div className="
        flex flex-col items-center justify-center

        gap-[2.5rem]
        lg:gap-[4.5rem]
        ">

          {showLogo &&
            <img
              ref={logoRef}
              src="/Logomark-None.svg"
              alt="Support lockup with no registration"
              className="h-[53px] lg:h-[68px]"
            />
          }
          <p
            ref={textRef}
            style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
            className={`
              text-center
              text-text-primary
              text-52-to-28--neue-june
            `}>
            {text}
          </p>
          <div ref={subheadingRef}>
            <Subheading size={isLg ? 24 : 20} subheading={subheading} alignment="center" />
          </div>
          <div
          ref={columnsContainerRef}
          style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
           className="flex flex-col lg:flex-row gap-[2.5rem]">
            <div className={`
              w-full min-w-[18rem]
              py-0 lg:py-[0.5rem]
              flex flex-col items-center justify-center gap-[1.25rem] lg:gap-[1.5rem]
              ${isTb ? 'text-center' :  language === 'arabic' ? 'text-right' : 'text-left'}
              `}>
              <p className="text-text-primary text-28--neue-june w-full" style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>{column1Heading}</p>
              <p className="text-text-tertiary text-18--alexandria w-full tb:max-w-[43.3125rem] lg:max-w-full" style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>{column1Description}</p>
              {(column1Benefits.length > 0 || benefits.length > 0) && (
                <div className={`w-full flex flex-col ${isTb ? 'items-center' : 'items-start'} gap-[0.5rem]`}>
                  {(column1Benefits.length > 0 ? column1Benefits : benefits).map((benefit, index) => (
                    <div key={index} className="flex flex-row items-start gap-[0.5rem]">
                      <div className="p-[0.125rem]">
                        <Check className="text-graphics-tertiary" size={isLg ? 20 : 16} />
                      </div>
                      <p className="text-text-quaternary text-16-to-14--alexandria" style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>
                        {benefit.content}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {isLg && (
              <div
                className="hidden lg:block min-w-[1.5px] w-[1.5px] bg-stroke-soft-200 rounded"
              />
            )}

            <div className={`
              w-full min-w-[18rem]
              py-0 lg:py-[0.5rem]
              flex flex-col items-center justify-center gap-[1.25rem] lg:gap-[1.5rem]
              ${isTb ? 'text-center' :  language === 'arabic' ? 'text-right' : 'text-left'}
              `}>
              <p className="text-text-primary text-28--neue-june w-full" style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>{column2Heading}</p>
              <p className="text-text-tertiary text-18--alexandria w-full tb:max-w-[43.3125rem] lg:max-w-full" style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>{column2Description}</p>
              {(column2Benefits.length > 0 || benefits.length > 0) && (
                <div className={`w-full flex flex-col ${isTb ? 'items-center' : 'items-start'} gap-[0.5rem]`}>
                  {(column2Benefits.length > 0 ? column2Benefits : benefits).map((benefit, index) => (
                    <div key={index} className="flex flex-row items-start gap-[0.5rem]">
                      <div className="p-[0.125rem]">
                        <Check className="text-graphics-tertiary" size={isLg ? 20 : 16} />
                      </div>
                      <p className="text-text-quaternary text-16-to-14--alexandria" style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>
                        {benefit.content}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}


export default WaitlistSection;
